import { COLOR_SCHEME_SELECTOR, darkPalette, lightPalette } from '@limitless-minds/ui-styles'
import type { PaletteOptions } from '@mui/material/styles'
import { createTheme } from '@mui/material/styles'

import { muiSlots } from './muiSlots'
import type { RoleColors } from './roles'
import { dark, light, roleColors } from './roles'

/**
 * The 13 roles as resolved colours, which is the form a component can actually read.
 *
 * `roles.ts` names a primitive and `ui-styles` holds the hex, so a role takes two hops to become a colour
 * - and a component can make neither of them: `no-restricted-imports` keeps the `primitives` import inside
 * this directory, because a component that reaches a hue directly skips the step where the contrast is
 * decided. Resolving the hops here is what closes that gap, and it has to be a palette group rather than a
 * hook: MUI already owns the machinery that picks the active scheme, so a group inside `colorSchemes`
 * follows the mode, emits `--mui-palette-role-*` for both schemes, and stays a plain string in JS - which
 * is what the `alpha()` call sites need, since `alpha()` throws on a `var(...)` rather than returning
 * something wrong.
 *
 * So the direction a component travels is `theme.palette.role.onSurfaceVariant`, which reads at the same
 * depth as a design-system token and therefore changes nothing about the shape of a call site.
 *
 * Declared here rather than in `src/types/mui.d.ts`, which derives its token names from the package
 * itself: a role is not a design-system token, so it has no counterpart there to derive from.
 */
declare module '@mui/material/styles' {
  interface Palette {
    role: RoleColors
  }
  interface PaletteOptions {
    role?: RoleColors
  }
}

/**
 * IMPORTANT: this module has to stay pure. `verify:color-mode` transpiles and imports it under Node to
 * assert what the built theme holds, so a read of `document` or `window` at module scope makes the gate
 * report that it cannot build the theme - which is true, and says nothing about the defect it is
 * looking for. The cookie is read inside `ColorSchemeProvider`, where a browser exists.
 */

/**
 * The design-system tokens carry a value per scheme, which is what re-themes the `ui-*` packages.
 *
 * Those packages read the tokens by name off this provider and know nothing about roles, so a token's
 * value is the only thing that reaches them. `ui-styles` owns both halves and guarantees the two carry
 * identical key sets, which this merge depends on: it is shallow at one level, so `palette.secondary` is
 * replaced whole, and a key present in one scheme and missing from the other would read `undefined`
 * through `theme.palette` while its CSS variable kept the other scheme's fallback - two different wrong
 * answers, neither of which throws.
 *
 * Components in this repo read the roles rather than these tokens, and a site here still painting a
 * design-system token is a bug rather than a pending migration.
 */
const schemeTokens: Record<'light' | 'dark', PaletteOptions> = { light: lightPalette, dark: darkPalette }

const scheme = (mode: 'light' | 'dark'): PaletteOptions => {
  const tokens = schemeTokens[mode]
  // Resolved once per scheme and handed to both consumers, so the two-hop role-to-hex lookup has one
  // implementation rather than one here and one in `muiSlots`.
  const colors = roleColors(mode === 'dark' ? dark : light)
  const slots = muiSlots(colors)
  // `primary` is merged rather than spread, because this spread is shallow and that family carries the
  // design-system tokens the `ui-*` packages look up by name on this provider. Replacing it whole drops
  // them, and nothing would say so: the palette gates read the installed package, not the built theme.
  return { ...tokens, ...slots, primary: { ...(tokens.primary as object), ...slots.primary }, role: colors }
}

/**
 * `colorSchemeSelector` is not optional. Its default is `'media'`, and with that default MUI writes no
 * attribute at all, so nothing can select a scheme deliberately - no error and no warning, just a theme
 * that only ever follows the operating system. The value comes from `ui-styles` rather than from a
 * constant here, because `index.html` has to write the same attribute and a stylesheet cannot import: one
 * owner for the string means a rename cannot leave the two disagreeing.
 */
export const theme = createTheme({
  cssVariables: { colorSchemeSelector: COLOR_SCHEME_SELECTOR },
  colorSchemes: { light: { palette: scheme('light') }, dark: { palette: scheme('dark') } },
})
